import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import TesterDashboard from "@/pages/TesterDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Home from "@/pages/Home";
import Pricing from "@/pages/Pricing";
import PaymentReturn from "@/pages/PaymentReturn";
import Status from "@/pages/Status";
import Session from "@/pages/Session";
import Activate from "@/pages/Activate";
import Report from "@/pages/Report";
import Admin from "@/pages/Admin";
import Join from "@/pages/Join";
import Unsubscribe from "@/pages/Unsubscribe";
import JobsHistory, { JobsRequest } from "@/pages/Jobs";
import Landing from "@/pages/Landing";
import Compare from "@/pages/Compare";
import CrowdsourcedTesting from "@/pages/CrowdsourcedTesting";
import Service from "@/pages/Service";
import Device from "@/pages/Device";
import Devices from "@/pages/Devices";
import Mcp from "@/pages/Mcp";
import About from "@/pages/About";
import Research from "@/pages/Research";
import ResearchReport from "@/pages/ResearchReport";
import ResearchEmbed from "@/pages/ResearchEmbed";
import HiJoin from "@/pages/HiJoin";
import { Terms, Privacy } from "@/pages/Legal";

function NotFound() {
  if (typeof document !== "undefined") document.title = "Page not found | 99tests";
  return (
    <div className="app-main"><div className="wrap" data-testid="not-found">
      <h1>Page not found</h1>
      <p className="lede">That page doesn't exist. Try one of these instead:</p>
      <p style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <Link className="btn" to="/">Home</Link>
        <Link className="btn ghost" to="/pricing">Pricing</Link>
        <Link className="btn ghost" to="/research">Research</Link>
        <a className="btn ghost" href="https://blog.99tests.com/">Blog</a>
      </p>
    </div></div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: process the OAuth session_id in the URL fragment before anything else.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/tester" element={<TesterDashboard />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/crowdsourced-testing" element={<CrowdsourcedTesting />} />
        <Route path="/mobile-app-testing-india" element={<Service slug="mobile-app-testing-india" />} />
        <Route path="/cross-browser-testing" element={<Service slug="cross-browser-testing" />} />
        <Route path="/exploratory-testing" element={<Service slug="exploratory-testing" />} />
        <Route path="/test-app-on/:slug" element={<Device />} />
        <Route path="/devices" element={<Devices />} />
        <Route path="/real-device-testing-india" element={<Landing slug="real-device-testing-india" />} />
        <Route path="/test-ai-generated-test-cases" element={<Landing slug="test-ai-generated-test-cases" />} />
        <Route path="/vs-device-clouds-and-automation" element={<Landing slug="vs-device-clouds-and-automation" />} />
        <Route path="/vs/utest" element={<Compare slug="utest" />} />
        <Route path="/vs/testlio" element={<Compare slug="testlio" />} />
        <Route path="/vs/browserstack-app-live" element={<Compare slug="browserstack-app-live" />} />
        <Route path="/vs/test-io" element={<Compare slug="test-io" />} />
        <Route path="/vs/global-app-testing" element={<Compare slug="global-app-testing" />} />
        <Route path="/payment/success" element={<PaymentReturn />} />
        <Route path="/payment/cancel" element={<PaymentReturn cancelled />} />
        <Route path="/status/:token" element={<Status />} />
        <Route path="/session/:token" element={<Session />} />
        <Route path="/activate/:token" element={<Activate />} />
        <Route path="/report/:token" element={<Report />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/join" element={<Join />} />
        <Route path="/hi/join" element={<HiJoin />} />
        <Route path="/mcp-server" element={<Mcp />} />
        <Route path="/about" element={<About />} />
        <Route path="/research" element={<Research />} />
        <Route path="/research/:slug" element={<ResearchReport />} />
        <Route path="/embed/research/:slug/:chartId" element={<ResearchEmbed />} />
        <Route path="/unsubscribe/:id" element={<Unsubscribe />} />
        <Route path="/jobs" element={<JobsRequest />} />
        <Route path="/jobs/:token" element={<JobsHistory />} />
        <Route path="/terms" element={<Terms which="client" />} />
        <Route path="/tester-terms" element={<Terms which="tester" />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="bottom-center" richColors />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
